import React from 'react';
import { Link } from 'react-router-dom';
import CompetitionCard from './CompetitionCard';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import Button from '../common/Button';
import { useAuth } from '../../hooks/useAuth';
import './CompetitionList.css';

/**
 * CompetitionList component
 * Displays a list of all competitions
 * T063: Competition list display for public viewing
 */
const CompetitionList = ({ competitions, loading, error, onRetry }) => {
  const { isOrganizer, isAuthenticated } = useAuth();

  if (loading) {
    return <Loading size="large" />;
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        variant="error"
        onRetry={onRetry}
      />
    );
  }

  if (!competitions || competitions.length === 0) {
    return (
      <div className="competition-list-empty">
        <div className="empty-state-icon">🏆</div>
        <h2>No Competitions Yet</h2>
        <p>There are no pool tournaments or leagues at this time.</p>
        {isOrganizer ? (
          <>
            <p>Get started by creating your first competition!</p>
            <Link to="/organizer/competitions/create" className="btn btn-primary">
              Create Competition
            </Link>
          </>
        ) : isAuthenticated ? (
          <p>Contact your league organiser to create a competition.</p>
        ) : (
          <>
            <p>Sign in to view exclusive competitions or contact your league administrator.</p>
            <Link to="/login" className="btn btn-secondary">
              Sign In
            </Link>
          </>
        )}
      </div>
    );
  }

  // Group competitions by status
  const activeCompetitions = competitions.filter(c => c.status === 'active');
  const upcomingCompetitions = competitions.filter(c => c.status === 'upcoming');
  const completedCompetitions = competitions.filter(c => c.status === 'completed');

  return (
    <div className="competition-list">
      {activeCompetitions.length > 0 && (
        <section className="competition-section">
          <h2>Active Competitions</h2>
          <div className="competition-grid">
            {activeCompetitions.map((competition) => (
              <CompetitionCard key={competition.competitionId} competition={competition} />
            ))}
          </div>
        </section>
      )}

      {upcomingCompetitions.length > 0 && (
        <section className="competition-section">
          <h2>Upcoming Competitions</h2>
          <div className="competition-grid">
            {upcomingCompetitions.map((competition) => (
              <CompetitionCard key={competition.competitionId} competition={competition} />
            ))}
          </div>
        </section>
      )}

      {completedCompetitions.length > 0 && (
        <section className="competition-section">
          <h2>Completed Competitions</h2>
          <div className="competition-grid">
            {completedCompetitions.map((competition) => (
              <CompetitionCard key={competition.competitionId} competition={competition} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default CompetitionList;